import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  ArrowLeft,
  Building,
  Calendar,
  Download,
  Edit,
  ExternalLink,
  FileDown,
  FileText,
  Hash,
  MapPin,
  Menu,
  Phone,
  Target,
  Users
} from 'lucide-react';
import './App.css';
import './ClientDetail.css';
import AppSidebar from './components/AppSidebar';
import NotificationBell from './components/NotificationBell';
import { downloadClientPackage, isExternalLink } from './utils/clientPdf';
import { logout as endSession } from './auth';

const API_ROOT = process.env.NODE_ENV === 'production'
  ? (process.env.REACT_APP_API_URL || '')
  : (process.env.REACT_APP_API_URL || 'http://localhost:5001');
const API_BASE_URL = API_ROOT ? `${API_ROOT}/api` : '/api';

const STATUS_LABELS = {
  inward: 'Inward',
  in_progress: 'In Progress',
  outward: 'Outward',
  completed: 'Completed'
};

const ATTACH_TYPE_LABELS = {
  drawing: 'Architect Drawing',
  document: 'Document',
  application: 'Application'
};

const APPROVAL_LABELS = {
  pending: 'Pending review',
  needs_revision: 'Needs revision',
  approved: 'Approved'
};

const APPROVAL_OPTIONS = [
  { value: 'pending', label: 'Pending review' },
  { value: 'needs_revision', label: 'Needs revision' },
  { value: 'approved', label: 'Approved' }
];

const formatDate = (value) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return date.toLocaleDateString();
};

const formatSize = (bytes) => {
  const size = Number(bytes) || 0;
  if (size <= 0) return '';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  return `${(size / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export default function ClientDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const currentRole = localStorage.getItem('role') || '';
  const isAdmin = currentRole === 'admin';
  const canEditClient = isAdmin || currentRole === 'architect';
  const [client, setClient] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [remarkDrafts, setRemarkDrafts] = useState({});
  const [approvalDrafts, setApprovalDrafts] = useState({});
  const [savingRemarkId, setSavingRemarkId] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    }
  }, []);

  const loadClient = useCallback(async () => {
    try {
      setLoading(true);
      const [clientRes, attachRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/clients/${id}`),
        axios.get(`${API_BASE_URL}/clients/${id}/attachments`)
      ]);
      const rows = attachRes.data || [];
      setClient(clientRes.data);
      setAttachments(rows);
      const drafts = {};
      const approvals = {};
      rows.forEach((row) => {
        if (row.attach_type === 'drawing') {
          drafts[row.id] = row.remark || '';
          approvals[row.id] = row.approval_status || 'pending';
        }
      });
      setRemarkDrafts(drafts);
      setApprovalDrafts(approvals);
    } catch (error) {
      const message = error?.response?.data?.error || 'Failed to load client';
      toast.error(message);
      if (error?.response?.status === 404) {
        setTimeout(() => navigate('/dashboard'), 1200);
      }
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    loadClient();
  }, [loadClient]);

  const logout = () => {
    endSession();
  };

  const openAttachment = async (attachment, download = false) => {
    if (isExternalLink(attachment.attach_link)) {
      window.open(attachment.attach_link, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/clients/${id}/attachments/${attachment.id}/file`,
        {
          params: download ? { download: '1' } : {},
          responseType: 'blob'
        }
      );
      const blobUrl = URL.createObjectURL(response.data);
      if (download) {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = attachment.attach_name || 'attachment';
        link.click();
      } else {
        window.open(blobUrl, '_blank', 'noopener,noreferrer');
      }
      setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
    } catch (error) {
      toast.error('Failed to open attachment');
    }
  };

  const saveRemark = async (attachment) => {
    if (!isAdmin || attachment.attach_type !== 'drawing') return;
    const remark = String(remarkDrafts[attachment.id] ?? '').trim();
    const approval_status = approvalDrafts[attachment.id] || 'pending';
    if (approval_status === 'needs_revision' && !remark) {
      toast.error('Add a remark when marking Needs revision');
      return;
    }
    try {
      setSavingRemarkId(attachment.id);
      const { data } = await axios.patch(
        `${API_BASE_URL}/clients/${id}/attachments/${attachment.id}/remark`,
        { remark, approval_status }
      );
      setAttachments((prev) =>
        prev.map((row) =>
          row.id === attachment.id
            ? {
                ...row,
                remark: data.remark,
                remark_by: data.remark_by,
                remark_at: data.remark_at,
                approval_status: data.approval_status
              }
            : row
        )
      );
      setRemarkDrafts((prev) => ({ ...prev, [attachment.id]: data.remark || '' }));
      setApprovalDrafts((prev) => ({
        ...prev,
        [attachment.id]: data.approval_status || 'pending'
      }));
      toast.success('Drawing review saved');
    } catch (error) {
      toast.error(error?.response?.data?.error || 'Failed to save review');
    } finally {
      setSavingRemarkId(null);
    }
  };

  const handleDownloadPackage = async () => {
    if (!client) return;
    try {
      setPdfLoading(true);
      await downloadClientPackage({
        client,
        attachments,
        fetchAttachment: async (attachment) => {
          if (isExternalLink(attachment.attach_link)) {
            try {
              const response = await fetch(attachment.attach_link);
              if (!response.ok) {
                return { skipped: true, reason: 'External file not directly downloadable' };
              }
              const blob = await response.blob();
              return {
                blob,
                contentType: response.headers.get('content-type') || blob.type
              };
            } catch {
              return { skipped: true, reason: 'External file not directly downloadable' };
            }
          }

          const response = await axios.get(
            `${API_BASE_URL}/clients/${id}/attachments/${attachment.id}/file`,
            {
              params: { download: '1' },
              responseType: 'blob'
            }
          );
          return {
            blob: response.data,
            contentType: response.headers['content-type'] || response.data?.type
          };
        }
      });
      toast.success('ZIP package downloaded');
    } catch (error) {
      console.error(error);
      toast.error('Failed to download ZIP package');
    } finally {
      setPdfLoading(false);
    }
  };

  const detailFields = client
    ? [
        { label: 'Client ID', value: client.client_id, icon: Hash },
        { label: 'Client Name', value: client.client_name, icon: Users },
        { label: 'Mobile Number', value: client.mobile_number, icon: Phone },
        { label: 'Customer Area', value: client.customer_area, icon: MapPin },
        { label: 'Firm Name', value: client.firm_name, icon: Building },
        { label: 'Plot No', value: client.plot_no, icon: Hash },
        {
          label: 'Plot Area',
          value: `${client.plot_area ?? '-'} ${client.plot_area_unit || ''}`.trim(),
          icon: MapPin
        },
        { label: 'Project Head', value: client.project_head, icon: Target },
        { label: 'Assigned Architect', value: client.assigned_architect || '-', icon: Users },
        { label: 'Inward Date', value: formatDate(client.inward_date), icon: Calendar },
        { label: 'Outward Date', value: formatDate(client.outward_date), icon: Calendar }
      ]
    : [];

  return (
    <div className="App client-detail-page">
      <ToastContainer theme="light" position="top-right" autoClose={3000} />
      <div className="app-shell">
        <AppSidebar
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          active="dashboard"
          onLogout={logout}
        />
        <div className="app-main">
          <header className="app-topbar">
            <div className="app-topbar-actions">
              <button
                type="button"
                className="btn btn-outline sidebar-toggle"
                onClick={() => setMobileOpen(true)}
                aria-label="Open menu"
              >
                <Menu size={18} />
                Menu
              </button>
              <h1 className="app-topbar-title">Client Profile</h1>
            </div>
            <div className="app-topbar-actions">
              <NotificationBell />
              {client && (
                <>
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={handleDownloadPackage}
                  disabled={pdfLoading}
                >
                  <FileDown size={16} />
                  {pdfLoading ? 'Preparing ZIP…' : 'Download ZIP'}
                </button>
                {canEditClient && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => navigate('/dashboard', { state: { editClientId: client.id } })}
                  >
                    <Edit size={16} /> {currentRole === 'architect' ? 'Architect Drawing' : 'Edit'}
                  </button>
                )}
                </>
              )}
            </div>
          </header>

          <div className="container">
            <div className="client-detail-toolbar">
              <button type="button" className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
                <ArrowLeft size={16} /> Back to Dashboard
              </button>
            </div>

        {loading ? (
          <div className="card client-detail-card">
            <div className="empty-state">
              <FileText size={48} />
              <h3>Loading client…</h3>
            </div>
          </div>
        ) : !client ? (
          <div className="card client-detail-card">
            <div className="empty-state">
              <FileText size={48} />
              <h3>Client not found</h3>
              <p>
                <Link to="/dashboard">Return to dashboard</Link>
              </p>
            </div>
          </div>
        ) : (
          <>
            <section className="card client-detail-card">
              <header className="client-detail-header">
                <div>
                  <p className="client-detail-eyebrow">Client profile</p>
                  <h2>{client.client_name}</h2>
                  <p className="client-detail-subtitle">
                    {client.client_id} · {client.customer_area} · {client.firm_name}
                  </p>
                </div>
                <span className={`status-badge status-${client.status || 'inward'}`}>
                  {STATUS_LABELS[client.status] || 'Inward'}
                </span>
              </header>

              <div className="client-detail-grid">
                {detailFields.map((field) => {
                  const Icon = field.icon;
                  return (
                    <div key={field.label} className="client-detail-field">
                      <div className="client-detail-field-label">
                        <Icon size={14} />
                        {field.label}
                      </div>
                      <div className="client-detail-field-value">{field.value || '-'}</div>
                    </div>
                  );
                })}
              </div>
            </section>

            <section className="card client-detail-card">
              <header className="client-detail-attachments-head">
                <div>
                  <h3>Attachments</h3>
                  <p>
                    {attachments.length === 0
                      ? 'No files attached yet.'
                      : `${attachments.length} file${attachments.length === 1 ? '' : 's'}`}
                    {currentRole === 'architect'
                      ? ' · Use Architect Drawing to upload or update drawings'
                      : ''}
                  </p>
                </div>
              </header>

              {attachments.length === 0 ? (
                <div className="empty-state client-detail-empty-attachments">
                  <FileText size={40} />
                  <h3>No attachments</h3>
                  <p>
                    {currentRole === 'architect'
                      ? 'Use Architect Drawing to upload a drawing for this client.'
                      : 'Use Edit Client to upload files or add external links.'}
                  </p>
                </div>
              ) : (
                <ul className="client-attachment-list">
                  {attachments.map((attachment) => {
                    const isDrawing = attachment.attach_type === 'drawing';
                    return (
                      <li key={attachment.id} className="client-attachment-item client-attachment-item-block">
                        <div className="client-attachment-row">
                          <div className="client-attachment-info">
                            <div className="client-attachment-icon" aria-hidden="true">
                              <FileText size={18} />
                            </div>
                            <div>
                              <p className="client-attachment-name">
                                {attachment.attach_name || 'Untitled document'}
                              </p>
                              <p className="client-attachment-meta">
                                <span className={`attach-type-badge attach-type-${attachment.attach_type || 'document'}`}>
                                  {ATTACH_TYPE_LABELS[attachment.attach_type] || 'Document'}
                                </span>
                                {isDrawing && (
                                  <>
                                    {' · '}
                                    <span className={`approval-badge approval-${attachment.approval_status || 'pending'}`}>
                                      {APPROVAL_LABELS[attachment.approval_status] || 'Pending review'}
                                    </span>
                                  </>
                                )}
                                {' · '}
                                {isExternalLink(attachment.attach_link) ? 'External link' : 'Uploaded file'}
                                {attachment.size_bytes ? ` · ${formatSize(attachment.size_bytes)}` : ''}
                                {attachment.uploaded_by ? ` · by ${attachment.uploaded_by}` : ''}
                                {attachment.created_at
                                  ? ` · ${formatDate(attachment.created_at)}`
                                  : ''}
                              </p>
                            </div>
                          </div>
                          <div className="client-attachment-actions">
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={() => openAttachment(attachment, false)}
                            >
                              {isExternalLink(attachment.attach_link) ? (
                                <ExternalLink size={14} />
                              ) : (
                                <FileText size={14} />
                              )}
                              Open
                            </button>
                            {!isExternalLink(attachment.attach_link) && (
                              <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => openAttachment(attachment, true)}
                              >
                                <Download size={14} />
                                Download
                              </button>
                            )}
                          </div>
                        </div>

                        {isDrawing && (
                          <div className="client-attachment-remark">
                            <div className="client-attachment-remark-label">Drawing review</div>
                            {isAdmin ? (
                              <>
                                <label className="client-attachment-remark-label" htmlFor={`approval-${attachment.id}`}>
                                  Approval status
                                </label>
                                <select
                                  id={`approval-${attachment.id}`}
                                  className="form-control"
                                  style={{ marginBottom: 10 }}
                                  value={approvalDrafts[attachment.id] || 'pending'}
                                  onChange={(e) =>
                                    setApprovalDrafts((prev) => ({
                                      ...prev,
                                      [attachment.id]: e.target.value
                                    }))
                                  }
                                >
                                  {APPROVAL_OPTIONS.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                  ))}
                                </select>
                                <label className="client-attachment-remark-label" htmlFor={`remark-${attachment.id}`}>
                                  Admin remark
                                </label>
                                <textarea
                                  id={`remark-${attachment.id}`}
                                  className="form-control client-attachment-remark-input"
                                  rows={3}
                                  maxLength={2000}
                                  value={remarkDrafts[attachment.id] ?? ''}
                                  onChange={(e) =>
                                    setRemarkDrafts((prev) => ({
                                      ...prev,
                                      [attachment.id]: e.target.value
                                    }))
                                  }
                                  placeholder="Add a remark for this Architect Drawing…"
                                />
                                <div className="client-attachment-remark-actions">
                                  <button
                                    type="button"
                                    className="btn btn-primary"
                                    disabled={savingRemarkId === attachment.id}
                                    onClick={() => saveRemark(attachment)}
                                  >
                                    {savingRemarkId === attachment.id ? 'Saving…' : 'Save review'}
                                  </button>
                                  {attachment.remark_by && attachment.remark_at && (
                                    <span className="client-attachment-remark-meta">
                                      Last saved by {attachment.remark_by} · {formatDate(attachment.remark_at)}
                                    </span>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                <p className="client-attachment-remark-empty" style={{ marginBottom: 8 }}>
                                  Status:{' '}
                                  <strong>
                                    {APPROVAL_LABELS[attachment.approval_status] || 'Pending review'}
                                  </strong>
                                </p>
                                {attachment.remark ? (
                                  <div className="client-attachment-remark-readonly">
                                    <p>{attachment.remark}</p>
                                    {attachment.remark_by && (
                                      <span className="client-attachment-remark-meta">
                                        By {attachment.remark_by}
                                        {attachment.remark_at ? ` · ${formatDate(attachment.remark_at)}` : ''}
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <p className="client-attachment-remark-empty">No admin remark yet.</p>
                                )}
                              </>
                            )}
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )}
            </section>
          </>
        )}
      </div>
        </div>
      </div>
    </div>
  );
}
